import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  Box,
  CssBaseline,
  ThemeProvider,
  createTheme,
  IconButton,
  Button,
  Tooltip,
  PaletteMode,
  Stack,
  Link as MuiLink,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import GitHubIcon from '@mui/icons-material/GitHub';
import { rememberSection } from '../../utils/lastTab';
import { PageWidthContext } from './PageWidth';

const GITHUB_URL = 'https://github.com/linkmodo';

const THEME_STORAGE_KEY = 'p2t-color-mode';

/** Brand colours, sampled from the PDF-To-Text.com logo. */
export const BRAND = {
  blue: '#0252C1',
  red: '#BC0E21',
  navy: '#182130',
} as const;

export const SITE_NAME = 'PDF-To-Text.com';

/** Build the app theme for a given light/dark mode. */
const createAppTheme = (mode: PaletteMode) =>
  createTheme({
    palette: {
      mode,
      // Dark mode uses lighter tints of the brand colours so text and buttons keep their contrast.
      primary: { main: mode === 'light' ? BRAND.blue : '#6EA8FE' },
      secondary: { main: mode === 'light' ? BRAND.red : '#FF6B7A' },
      ...(mode === 'light'
        ? { background: { default: '#f5f6f8' }, text: { primary: BRAND.navy } }
        : { background: { default: '#121212', paper: '#1e1e1e' } }),
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h4: { fontWeight: 600 },
    },
    components: {
      MuiButton: { styleOverrides: { root: { textTransform: 'none' } } },
    },
  });

/** Resolve the initial mode from saved preference, else OS preference. */
const getInitialMode = (): PaletteMode => {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {
    /* localStorage unavailable (private mode) — fall through */
  }
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

const NAV_LINKS = [
  { to: '/', label: 'Converter' },
  { to: '/text-editor', label: 'Text Editor' },
  { to: '/typing-test', label: 'Typing Test' },
  { to: '/guides', label: 'Guides' },
];

/**
 * Width from which the menu fits to the left of the centred logo (menu ≈ 350px +
 * half the logo's width on each side). Narrower screens put the menu on its own row.
 */
const INLINE_NAV_QUERY = '@media (min-width: 1024px)';

/** Whether `pathname` belongs to the section rooted at `to`. */
const isActivePath = (pathname: string, to: string): boolean =>
  to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);

/** The logo is a full-colour image on white; in dark mode it sits on a white badge so it stays legible. */
const BrandLogo: React.FC<{ dark: boolean }> = ({ dark }) => (
  <Box
    component={RouterLink}
    to="/"
    aria-label={`${SITE_NAME} home`}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      flexShrink: 0,
      borderRadius: 1.5,
      bgcolor: dark ? '#fff' : 'transparent',
      px: dark ? 1 : 0,
      py: dark ? 0.5 : 0,
      '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
    }}
  >
    <Box
      component="img"
      src="/brand/logo-header.png"
      srcSet="/brand/logo-header.png 1x, /brand/logo-header@2x.png 2x"
      alt={`${SITE_NAME} — free PDF, Word and Excel to text converter`}
      width={199}
      height={46}
      sx={{ display: 'block', height: { xs: 34, sm: 40, md: 46 }, width: 'auto' }}
    />
  </Box>
);

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mode, setMode] = useState<PaletteMode>(getInitialMode);
  // Pages (the text editor) can request the full browser width.
  const [fullWidth, setFullWidth] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, mode);
    } catch {
      /* ignore persistence failures */
    }
  }, [mode]);

  const theme = useMemo(() => createAppTheme(mode), [mode]);
  const dark = mode === 'dark';
  const location = useLocation();
  const { pathname } = location;

  // Remember the last tool used so the next visit can reopen it.
  useEffect(() => rememberSection(location), [location]);

  const navButtons = NAV_LINKS.map(({ to, label }) => {
    const active = isActivePath(pathname, to);
    return (
      <Button
        key={to}
        component={RouterLink}
        to={to}
        size="small"
        aria-current={active ? 'page' : undefined}
        sx={(t) => ({
          px: 1.5,
          fontWeight: active ? 700 : 500,
          color: active ? 'primary.main' : 'text.primary',
          bgcolor: active ? alpha(t.palette.primary.main, dark ? 0.16 : 0.1) : 'transparent',
          '&:hover': { bgcolor: alpha(t.palette.primary.main, dark ? 0.22 : 0.14) },
        })}
      >
        {label}
      </Button>
    );
  });
  const toggleMode = () => setMode((prev) => (prev === 'light' ? 'dark' : 'light'));

  return (
    <ThemeProvider theme={theme}>
      <PageWidthContext.Provider value={setFullWidth}>
        <CssBaseline />
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <AppBar
            position="static"
            elevation={0}
            color="inherit"
            sx={{
              bgcolor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
              // Thin brand strip: PDF red → Text blue, as in the logo's PDF ↔ TEXT pill.
              '&::before': {
                content: '""',
                display: 'block',
                height: 3,
                background: `linear-gradient(90deg, ${BRAND.red}, ${BRAND.blue})`,
              },
            }}
          >
            {/* Three columns: menu | centred logo | theme toggle. Equal side columns keep the logo exactly centred. */}
            <Toolbar
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto 1fr',
                alignItems: 'center',
                columnGap: 2,
                minHeight: { xs: 60, md: 68 },
              }}
            >
              <Stack
                component="nav"
                aria-label="Main"
                direction="row"
                spacing={0.5}
                sx={{ justifySelf: 'start', display: 'none', [INLINE_NAV_QUERY]: { display: 'flex' } }}
              >
                {navButtons}
              </Stack>
              <Box sx={{ gridColumn: 2, justifySelf: 'center' }}>
                <BrandLogo dark={dark} />
              </Box>
              <Box sx={{ gridColumn: 3, justifySelf: 'end' }}>
                <Tooltip title={dark ? 'Switch to light mode' : 'Switch to dark mode'}>
                  <IconButton onClick={toggleMode} aria-label="Toggle light/dark theme" color="inherit">
                    {dark ? <Brightness7Icon /> : <Brightness4Icon />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Toolbar>
            {/* Narrower screens: the links get their own row below the centred logo. */}
            <Stack
              component="nav"
              aria-label="Main"
              direction="row"
              spacing={0.5}
              justifyContent="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ display: 'flex', [INLINE_NAV_QUERY]: { display: 'none' }, pb: 1, px: 1 }}
            >
              {navButtons}
            </Stack>
          </AppBar>
          <Container component="main" maxWidth={fullWidth ? false : 'lg'} sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
            {children}
          </Container>
          <Box component="footer" sx={{ py: 1.5, px: 2, mt: 'auto', backgroundColor: 'background.paper', borderTop: '1px solid', borderColor: 'divider' }}>
            <Container maxWidth="sm">
              <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                {NAV_LINKS.map(({ to, label }) => (
                  <MuiLink key={to} component={RouterLink} to={to} variant="caption" color="text.secondary">{label}</MuiLink>
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'block' }}>
                © 2026 {SITE_NAME} · Li Fan ·{' '}
                <MuiLink
                  href={GITHUB_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="inherit"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, verticalAlign: 'bottom' }}
                >
                  <GitHubIcon sx={{ fontSize: '1rem' }} />
                  GitHub
                </MuiLink>
              </Typography>
            </Container>
          </Box>
        </Box>
      </PageWidthContext.Provider>
    </ThemeProvider>
  );
};

export default Layout;
